import { DateService } from "@/core/services/Date.service";
import { DateAdapter } from "@/infrastructure/adapters/dayJs";
import { BrandMember, BrandMemberProps } from "@/core/entities/lookup";

export interface DeductionActivityProps {
  id: string;
  user: BrandMemberProps;
  createdOn: string;
  logs: LogProps[];
}

export interface FieldDataProps {
  fieldName: string;
  oldValue: string;
  newValue: string;
}

export interface LogProps {
  message: string | null;
  fieldData: FieldDataProps[] | null;
  fileList: string[] | null;
}

class DeductionActivity implements DeductionActivityProps {
  private readonly _id: string;
  private readonly _user: BrandMember;
  private readonly _createdOn: string;
  private readonly _logs: DeductionActivityLog[];

  constructor(deductionActivity: DeductionActivityProps) {
    this._id = deductionActivity.id;
    this._user = BrandMember.from(deductionActivity.user);
    this._createdOn = deductionActivity.createdOn;
    this._logs = deductionActivity.logs.map(log => DeductionActivityLog.from(log));
  }

  get id(): string {
    return this._id;
  }

  get user(): BrandMember {
    return this._user;
  }

  get createdOn(): string {
    return this._createdOn;
  }

  public createdOnFormatted(format: string): string {
    return new DateService(new DateAdapter()).format(this._createdOn, format);
  }

  get logs(): DeductionActivityLog[] {
    return this._logs;
  }

  static from(deductionActivity: DeductionActivityProps): DeductionActivity {
    return new DeductionActivity(deductionActivity);
  }
}

class DeductionActivityLog implements LogProps {
  private readonly _message: string | null;
  private readonly _fieldData: DeductionActivityFieldData[] | null;
  private readonly _fileList: string[] | null;

  constructor(deductionActivityLog: LogProps) {
    this._message = deductionActivityLog.message;
    this._fieldData = deductionActivityLog.fieldData?.map(field => DeductionActivityFieldData.from(field)) ?? null;
    this._fileList = deductionActivityLog.fileList;
  }

  get message(): string | null {
    return this._message;
  }

  get fieldData(): DeductionActivityFieldData[] | null {
    return this._fieldData;
  }

  get fileList(): string[] | null {
    return this._fileList;
  }

  static from(deductionActivityLog: LogProps): DeductionActivityLog {
    return new DeductionActivityLog(deductionActivityLog);
  }
}

class DeductionActivityFieldData implements FieldDataProps {
  private readonly _fieldName: string;
  private readonly _oldValue: string;
  private readonly _newValue: string;

  constructor(deductionActivityFieldData: FieldDataProps) {
    this._fieldName = deductionActivityFieldData.fieldName;
    this._oldValue = deductionActivityFieldData.oldValue;
    this._newValue = deductionActivityFieldData.newValue;
  }

  get fieldName(): string {
    return this._fieldName;
  }

  get oldValue(): string {
    return this._oldValue;
  }

  get newValue(): string {
    return this._newValue;
  }

  static from(deductionActivityFieldData: FieldDataProps): DeductionActivityFieldData {
    return new DeductionActivityFieldData(deductionActivityFieldData);
  }
}

export { DeductionActivity, DeductionActivityLog, DeductionActivityFieldData };
